/**
 * Реестр мини-игр и поиск по триггеру.
 *
 * `MINIGAMES` — все определения (разделы E/F/G добавляются в Tasks 3–7
 * плана 2b). `minigamesFor` возвращает определения, у которых есть
 * подходящий триггер и `available()` даёт true для этой жизни; легальность
 * азартных игр берётся из `@life/data` по стране жизни. Мёртвая жизнь
 * мини-игр не получает. `gamblingEnabled: false` (учебный режим) выключает
 * раздел E целиком.
 */
import { gamblingLaw, type Dataset } from '@life/data';
import type { Life } from '@life/engine';
import type { MinigameDef, Trigger } from './types';
import { CASINO_MINIGAMES } from './casino/index';
import { MONEY_MINIGAMES } from './money/index';
import { SKILL_MINIGAMES } from './skill/index';
import { LIFE1_MINIGAMES, LIFE2_MINIGAMES } from './life/index';

export const MINIGAMES: MinigameDef[] = [...CASINO_MINIGAMES, ...MONEY_MINIGAMES, ...SKILL_MINIGAMES, ...LIFE1_MINIGAMES, ...LIFE2_MINIGAMES];

export interface MinigamesForOptions {
  /** Подмена реестра (тесты). */
  defs?: readonly MinigameDef[];
  /** `settings.gamblingEnabled`; по умолчанию true. */
  gamblingEnabled?: boolean;
}

/**
 * Совпадает ли триггер определения `def` с запросом `want` для этой жизни.
 * Для `age`: диапазон запроса не важен — проверяется возраст жизни,
 * период `everyYears` (от `min`) и фокус, если задан.
 */
export function triggerMatches(def: Trigger, want: Trigger, life: Life): boolean {
  if (def.kind !== want.kind) return false;
  switch (def.kind) {
    case 'decision':
      return want.kind === 'decision' && want.decision === def.decision;
    case 'event':
      return want.kind === 'event' && want.event === def.event;
    case 'envelope':
      return true;
    case 'place':
      return false;
    case 'age': {
      const { age } = life;
      if (age < def.min || age > def.max) return false;
      if (def.focus !== undefined && life.focus !== def.focus) return false;
      const every = def.everyYears ?? 1;
      return (age - def.min) % every === 0;
    }
  }
}

export function minigamesFor(trigger: Trigger, life: Life, ds: Dataset, opts: MinigamesForOptions = {}): MinigameDef[] {
  if (!life.alive) return [];
  const defs = opts.defs ?? MINIGAMES;
  const gamblingEnabled = opts.gamblingEnabled ?? true;
  const { law } = gamblingLaw(life.iso3);
  return defs.filter(
    (def) =>
      (gamblingEnabled || def.section !== 'E') &&
      def.triggers.some((t) => triggerMatches(t, trigger, life)) &&
      def.available(life, ds, law),
  );
}

export function minigameDef(id: string, defs: readonly MinigameDef[] = MINIGAMES): MinigameDef {
  const def = defs.find((d) => d.id === id);
  if (!def) throw new RangeError(`minigameDef: unknown minigame ${id}`);
  return def;
}
